<template>
  <multipane>
    <el-table
        :data="dictionaryView"
        row-key="id"
        :height="height"
        :style="{width: '30%', maxWidth: '500px', flex: 'none'}"
        ref="dictionaryTable"
        @current-change="handleDictionarySelectionChange"
        highlight-current-row>
      <el-table-column
          label="個別辞書名称"
          prop="name">
        <template #default="{row}">
          <table-inline>
            <dictionary-name-field :value="row"></dictionary-name-field>
          </table-inline>
        </template>
      </el-table-column>
    </el-table>
    <multipane-resizer></multipane-resizer>
    <el-table
        :data="serviceComponentModelView"
        row-key="id"
        :empty-text="selectedDictionary ?
          '選択した辞書にはサービス構成モデルが存在しません' :
          '個別辞書を選択すると、こちら側にサービス構成モデルが表示されます'
        "
        :height="height"
        @current-change="handleSelectionChange"
        ref="dcmTable"
        highlight-current-row>
      <el-table-column
          label="名称"
          prop="name"
          width="200">
        <template #default="{row}">
          <table-inline>
            <text-field-like
                :value="row.name">
              <template #prepend>
                <update-icon
                    :type="row.updateType"
                    class="mt-1"
                    small>
                </update-icon>
              </template>
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>
      <el-table-column
          label="説明"
          prop="description">
        <template #default="{row}">
          <table-inline>
            <description-input
                :value="row.description"
                readonly>
            </description-input>
          </table-inline>
        </template>
      </el-table-column>
      <el-table-column
          width="100"
          label="登録日"
          prop="createdAt">
        <template #default="{row}">
          {{ formatDate(row.createdAt) }}
        </template>
      </el-table-column>
    </el-table>
  </multipane>
</template>

<script>
import {Multipane, MultipaneResizer} from "vue-multipane";
import {mapGetters} from "vuex";
import gql from "graphql-tag";
import TableInline from "@/components/atoms/TableInline.vue";
import DescriptionInput from "@/components/molecules/DescriptionInput.vue";
import natsort from "natsort";
import Formatter from "@/utils/Formatter";
import DictionaryNameField from "@/components/molecules/DictionaryNameField.vue";
import IdInfo from "@/store/modules/hds/IdInfo";
import UpdateIcon from "@/components/molecules/UpdateIcon.vue";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";

const sortFn = natsort();
export default {
  name: 'ServiceComponentModelTable',
  mixins: [
    Formatter
  ],
  components: {
    TextFieldLike,
    UpdateIcon,
    DictionaryNameField,
    DescriptionInput,
    TableInline,
    Multipane,
    MultipaneResizer
  },
  inject: {
    form: {default: null},
    dialog: {default: null},
  },
  props: {
    value: {
      type: String,
      default: null
    },
    existingServiceImplementations: {
      type: Array,
      default: () => ([])
    },
    selectedControllerId: {
      type: String,
      default: null
    },
    height: {
      type: [Number, String],
    },
  },
  data() {
    return {
      dictionaries: [],
      selectedDictionary: null,
      selectedServiceComponentModel: null
    };
  },
  created() {
    this.form && this.form.register(this);
    this.client
        .query({
          query: gql`
            query ServiceComponentModelTable {
                dictionary: dictionariesByTypes(dictionaryTypes: [local, external]) {
                    id
                    name
                    description
                    dictionaryType
                    serviceComponentModels {
                        id
                        name
                        description
                        createdAt
                        serviceTranslationMaps {
                            targetServiceComponentModelId
                        }
                    }
                }
            }`
        })
        .then(response => {
          this.dictionaries = response.data.dictionary;
        });
  },
  beforeDestroy() {
    this.form && this.form.unregister(this)
  },
  computed: {
    ...mapGetters({
      client: 'hds/gqlClient',
    }),
    dictionaryView() {
      return this.dictionaries
          .slice()
          .sort((a, b) => sortFn(a.name, b.name));
    },
    serviceComponentModelView() {
      if (this.selectedDictionary) {
        return this.selectedDictionary.serviceComponentModels
            .filter(dcm => dcm.serviceTranslationMaps.length > 0)
            .sort((a, b) => sortFn(a.name, b.name))
            .map(dcm => this.buildServiceComponentModelView(dcm));
      } else {
        return [];
      }
    },
    hasError() {
      if(this.selectedServiceComponentModel) {
        return this.selectedServiceComponentModel.updateType === 'stay';
      }
      return true;
    },
    existingServiceImplementationsView() {
      return this.existingServiceImplementations.map(di => ({
        ...di,
        idInfo: IdInfo.of(di.serviceComponentModelId)
      }));
    },
  },
  methods: {
    buildServiceComponentModelView(scm) {
      if(!scm) {
        return null;
      }
      const scmIdInfo = IdInfo.of(scm.id);
      const existingIdInfo = this.existingServiceImplementationsView.find(di =>
          di.controllerId === this.selectedControllerId &&
          di.idInfo.elementId === scmIdInfo.elementId);
      return {
        ...scm,
        updateType: (() => {
          if(!existingIdInfo) {
            return 'add';
          } else if(existingIdInfo.idInfo.version === scmIdInfo.version) {
            return 'stay';
          } else {
            return 'update';
          }
        })(),
      };
    },
    handleDictionarySelectionChange(row) {
      this.selectedDictionary = row;
      this.selectedServiceComponentModel = null;
      this.$refs.dcmTable?.setCurrentRow();
    },
    handleSelectionChange(row) {
      this.selectedServiceComponentModel = row;
      this.$emit('input', row?.id ?? null);
      this.$emit('select', row);
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        if((this.selectedServiceComponentModel?.id ?? null) === (value ?? null)) {
          return;
        }
        this.selectedDictionary = null;
        this.selectedServiceComponentModel = null;
        for(const d of this.dictionaries) {
          const scm = d.serviceComponentModels?.find(dcm => dcm.id === value);
          if(scm) {
            this.selectedDictionary = d;
            this.selectedServiceComponentModel = this.buildServiceComponentModelView(scm);
          }
        }
      }
    },
    existingServiceImplementations: {
      handler() {
        if(this.selectedServiceComponentModel) {
          this.selectedServiceComponentModel = this.buildServiceComponentModelView(this.selectedServiceComponentModel);
        }
      }
    },
    'dialog.visible': {
      handler(visible) {
        if(!visible) {
          this.selectedDictionary = null;
          this.$refs.dictionaryTable?.setCurrentRow();
          this.$refs.dcmTable?.setCurrentRow();
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .el-table__empty-text {
  line-height: normal;
  width: 100%;
}
</style>
